import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import Notificaciones from './Notificaciones';

const Header = ({ authenticated }) => {
    const token = localStorage.getItem("AuthToken");
    const location = useLocation();

    let tokenInfo = {};
    if (token) {
        tokenInfo = jwtDecode(token);
    }

    const userRole = tokenInfo.role || null;

    const [isScrolled, setIsScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Cerrar dropdown al cambiar de ruta
    useEffect(() => {
        setActiveDropdown(null);
    }, [location.pathname]);

    const handleLinkClick = () => {
        window.scrollTo({ top: 0 });
        setActiveDropdown(null);
        const checkbox = document.getElementById("menu-toggle");
        if (checkbox) checkbox.checked = false;
    };

    const handleLogout = () => {
        localStorage.removeItem("AuthToken");
        window.location.reload();
    };

    const toggleDropdown = (name) => {
        setActiveDropdown(prev => prev === name ? null : name);
    };

    // ── Definición de menús por rol ──────────────────────────────────────────
    const menuUsuario = [
        { label: 'Mis tickets', path: '/dashboard' },
        { label: 'Nuevo ticket', path: '/nuevo-ticket' },
        { label: 'Roadmap', path: '/dashboard/roadmap' },
    ];

    const menuTecnico = [
        { label: 'Gestión de tickets', path: '/dashboard/tickets' },
        { label: 'Gestión de usuarios', path: '/dashboard/users' },
        { label: 'Roadmap', path: '/dashboard/roadmap' },
    ];

    const menuAdmin = [
        {
            label: 'Gestión',
            submenu: [
                { label: 'Tickets', path: '/dashboard/tickets' },
                { label: 'Usuarios', path: '/dashboard/users' },
                { label: 'Notificaciones', path: '/dashboard/notificaciones' },
            ]
        },
        {
            label: 'Proyectos',
            submenu: [
                { label: 'Tracking proyectos', path: '/dashboard/tracking-proyectos' },
            ]
        },
        {
            label: 'Material',
            submenu: [
                { label: 'Equipos', path: '/dashboard/equipos' },
                { label: 'Periféricos', path: '/dashboard/perifericos' },
            ]
        },
        {
            label: 'Sistema',
            submenu: [
                { label: 'Ajustes del sistema', path: '/dashboard/ajustes-del-sistema' },
                { label: 'Roadmap', path: '/dashboard/roadmap' },
            ]
        },
    ];

    const isActive = (path) => location.pathname === path;
    const isDropdownActive = (items) => items.some(i => location.pathname === i.path);

    // ── Render menú central desktop ─────────────────────────────────────────
    const renderDesktopNav = () => {
        if (!authenticated || tokenInfo.estado === "verificar" || tokenInfo.estado === "suspendido") return null;

        if (userRole === 'admin') {
            return (
                <nav className="header-nav" ref={dropdownRef}>
                    {menuAdmin.map((group) => (
                        <div
                            key={group.label}
                            className={`nav-item has-dropdown ${activeDropdown === group.label ? 'open' : ''} ${isDropdownActive(group.submenu) ? 'active' : ''}`}
                        >
                            <button
                                className="nav-btn"
                                onClick={() => toggleDropdown(group.label)}
                                aria-expanded={activeDropdown === group.label}
                            >
                                {group.label}
                                <svg className="nav-chevron" viewBox="0 0 10 6" fill="none">
                                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <div className="nav-dropdown">
                                {group.submenu.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`nav-dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                                        onClick={handleLinkClick}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>
            );
        }

        if (userRole === 'tecnico') {
            return (
                <nav className="header-nav">
                    {menuTecnico.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-item nav-link ${isActive(item.path) ? 'active' : ''}`}
                            onClick={handleLinkClick}
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>
            );
        }

        // usuario
        return (
            <nav className="header-nav">
                {menuUsuario.map((item) => (
                    <Link
                        key={item.path + item.label}
                        to={item.path}
                        className={`nav-item nav-link ${isActive(item.path) ? 'active' : ''}`}
                        onClick={handleLinkClick}
                    >
                        {item.label}
                    </Link>
                ))}
            </nav>
        );
    };

    return (
        <header className={isScrolled ? 'scrolled' : ''}>
            <div className='site-width grid-header'>
                <Link to="/" className='logo-link' onClick={handleLinkClick}>
                    <img className='header-logo' src='/images/logoadhoc.svg' alt='Logo ADHOC'/>
                </Link>

                {/* Menú central con subapartados por rol */}
                {renderDesktopNav()}

                <div className="user-actions">
                    {authenticated ? (
                        <>
                            {tokenInfo.estado === "verificar" || tokenInfo.estado === "suspendido" ? '' :
                                <>
                                    <Notificaciones />
                                    <Link to="/nuevo-ticket" className='crear-ticket' onClick={handleLinkClick}>
                                        Crear ticket
                                    </Link>
                                </>
                            }
                            <Link to="/dashboard" className='dashboard' onClick={handleLinkClick}>
                                Dashboard
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link to="/iniciar-sesion" className='login' onClick={handleLinkClick}>
                                Iniciar sesión
                            </Link>
                            <Link to="/registrarse" className='new-user' onClick={handleLinkClick}>
                                Crear cuenta
                            </Link>
                        </>
                    )}
                </div>

                {/* Burger responsive */}
                <div className="grid-burger">
                    {authenticated ? (<Notificaciones />) : ''}
                    <div className="adhoc-burger">
                        <input type="checkbox" id="menu-toggle" />
                        <label htmlFor="menu-toggle" className="menu-icon">
                            <div className="line top"></div>
                            <div className="line bottom"></div>
                        </label>
                        <div className="menu-responsive">
                            <Link to="/" className='logo-link' onClick={handleLinkClick}>
                                <img className='lateral-logo' src='/images/logoadhoc.svg' alt='Logo ADHOC'/>
                            </Link>

                            <div className="user-actions">
                                {authenticated ? (
                                    <>
                                        {/* Links de navegación por rol */}
                                        {tokenInfo.estado !== "verificar" && tokenInfo.estado !== "suspendido" && (
                                            <>
                                                {userRole === 'admin' && (
                                                    <>
                                                        <hr className="menu-lateral-separator" />
                                                        <span className="menu-lateral-label">Gestión</span>
                                                        <Link to="/dashboard/tickets" className='dashboard' onClick={handleLinkClick}>Tickets</Link>
                                                        <Link to="/dashboard/users" className='dashboard' onClick={handleLinkClick}>Usuarios</Link>
                                                        <Link to="/dashboard/notificaciones" className='dashboard' onClick={handleLinkClick}>Notificaciones</Link>
                                                        <hr className="menu-lateral-separator" />
                                                        <span className="menu-lateral-label">Proyectos</span>
                                                        <Link to="/dashboard/tracking-proyectos" className='dashboard' onClick={handleLinkClick}>Tracking proyectos</Link>
                                                        <hr className="menu-lateral-separator" />
                                                        <span className="menu-lateral-label">Material</span>
                                                        <Link to="/dashboard/equipos" className='dashboard' onClick={handleLinkClick}>Equipos</Link>
                                                        <Link to="/dashboard/perifericos" className='dashboard' onClick={handleLinkClick}>Periféricos</Link>
                                                        <hr className="menu-lateral-separator" />
                                                        <span className="menu-lateral-label">Sistema</span>
                                                        <Link to="/dashboard/ajustes-del-sistema" className='dashboard' onClick={handleLinkClick}>Ajustes del sistema</Link>
                                                        <Link to="/dashboard/roadmap" className='dashboard' onClick={handleLinkClick}>Roadmap</Link>
                                                    </>
                                                )}
                                                {userRole === 'tecnico' && (
                                                    <>
                                                        <hr className="menu-lateral-separator" />
                                                        <Link to="/dashboard/tickets" className='dashboard' onClick={handleLinkClick}>Gestión de tickets</Link>
                                                        <Link to="/dashboard/users" className='dashboard' onClick={handleLinkClick}>Gestión de usuarios</Link>
                                                        <Link to="/dashboard/roadmap" className='dashboard' onClick={handleLinkClick}>Roadmap</Link>
                                                    </>
                                                )}
                                                {(!userRole || userRole === 'user') && (
                                                    <>
                                                        <hr className="menu-lateral-separator" />
                                                        <Link to="/dashboard/proyectos" className='dashboard' onClick={handleLinkClick}>Mis proyectos</Link>
                                                        <Link to="/dashboard/servicios" className='dashboard' onClick={handleLinkClick}>Mis servicios</Link>
                                                        <Link to="https://agenciaadhoc.com/reportes" target="_blank" className='dashboard' onClick={handleLinkClick}>Reportar un error</Link>
                                                        <Link to="/dashboard/roadmap" className='dashboard' onClick={handleLinkClick}>Roadmap</Link>
                                                    </>
                                                )}
                                            </>
                                        )}

                                        {/* Logout al fondo */}
                                        <div className="menu-lateral-acciones">
                                            {tokenInfo.estado === "verificar" || tokenInfo.estado === "suspendido" ? '' :
                                                <>
                                                    <Link to="/dashboard" className='dashboard' onClick={handleLinkClick}>Dashboard</Link>
                                                    <Link to="/nuevo-ticket" className='crear-ticket' onClick={handleLinkClick}>Crear ticket</Link>
                                                </>
                                            }
                                            <button className='logout' onClick={handleLogout}>
                                                <img src="/images/cerrar-sesion.svg" alt="Logout" />
                                                <p>Salir</p>
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Link to="/iniciar-sesion" className='login' onClick={handleLinkClick}>Iniciar sesión</Link>
                                        <Link to="/registrarse" className='new-user' onClick={handleLinkClick}>Crear cuenta</Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
